import type { FC } from 'react'

/**
 * A degree number with its accidental drawn, not typed (Brian, 2026-10-23, after Joni's M1–M5 review): ♭ and ♯ in
 * the system fonts come out thinner than the bold numerals, and no common font has 𝄫, which showed as an empty
 * box. The shapes are stroked in the text color at the numeral's weight. Visual only: where a degree is read out,
 * `spokenText` names it in words.
 */

const accidentals = {
  '𝄫': 'double-flat',
  '𝄪': 'double-sharp',
  '♭': 'flat',
  '♯': 'sharp',
} as const

type Accidental = (typeof accidentals)[keyof typeof accidentals]

/** Shapes on a 16-unit-tall box, the baseline at 15, as wide as each needs */
const shapes: Record<Accidental, { width: number; path: string }> = {
  flat: { width: 8, path: 'M2 1V15M2 15C6.5 12.5 7.6 9 5.2 8C3.7 7.4 2.6 8.2 2 9' },
  sharp: { width: 10, path: 'M3.5 2.5V15.5M6.5 1V14M1 7L9 5.4M1 11.6L9 10' },
  'double-flat': {
    width: 14,
    path: 'M2 1V15M2 15C6.5 12.5 7.6 9 5.2 8C3.7 7.4 2.6 8.2 2 9M8 1V15M8 15C12.5 12.5 13.6 9 11.2 8C9.7 7.4 8.6 8.2 8 9',
  },
  'double-sharp': { width: 10, path: 'M2 6L8 12M8 6L2 12' },
}

export interface DegreeNumberProps {
  /** "♭7", "𝄫7", "♯11", "3": a leading accidental is drawn, the rest is text */
  value: string
}

export const DegreeNumber: FC<DegreeNumberProps> = props => {
  const { value } = props
  const [first = ''] = [...value]
  const accidental = accidentals[first as keyof typeof accidentals] as Accidental | undefined
  if (!accidental) {
    return <>{value}</>
  }
  const { width, path } = shapes[accidental]
  return (
    <>
      <svg
        data-accidental={accidental}
        aria-hidden="true"
        focusable="false"
        viewBox={`0 0 ${width} 16`}
        width={`${(width / 16).toFixed(3)}em`}
        height="1em"
        style={{ verticalAlign: '-0.06em', overflow: 'visible' }}
      >
        <path d={path} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" />
      </svg>
      {value.slice(first.length)}
    </>
  )
}
